import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  fetchSectionsAPI,
  reorderSectionsAPI,
  createSectionAPI,
  updateSectionAPI,
  deleteSectionAPI,
  getTestSeriesByIdAPI,
  exportQuestionsCsv,
} from "../../utils/api";
import { showDeleteConfirmation, showError, showSuccess } from "../../utils/sweetAlert";
import { EmptyState, ErrorAlert, LoadingState, Modal, StatCard } from "../../components/ui";
import ImportQuestionsModal from "../../components/ImportQuestionsModal";
import ImportHistoryModal from "../../components/ImportHistoryModal";
import SampleCsvMenu from "../../components/SampleCsvMenu";
import "./SectionsManager.css";

const EMPTY_FORM = {
  name: "",
  description: "",
  duration_minutes: "",
  total_questions: "",
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const isBlankHtml = (html) => !html || !html.replace(/<[^>]*>/g, "").trim();

// 0 when the section has no duration (allowed when the test has one timer)
const durationOf = (s) => Number(s.duration_minutes ?? s.duration ?? 0) || 0;
const targetOf = (s) => Number(s.total_questions ?? s.questions ?? 0) || 0;

// Swap two items of a list (a copy)
const swapped = (list, index, target) => {
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

function SectionsManager() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [testSeries, setTestSeries] = useState(null);
  // In test order: in a subject-wise test each subject is followed by its own sections (parent_id = the subject)
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // What the modal edits: { kind: "section" | "subject", section?: existing row, parent?: subject of a new section }
  const [editing, setEditing] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // The sections endpoint also says whether the test is split into subjects
  const [nestedFromSections, setNestedFromSections] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [testSeriesResponse, sectionsResponse] = await Promise.all([
        getTestSeriesByIdAPI(id),
        fetchSectionsAPI(id),
      ]);

      if (testSeriesResponse?.success) {
        setTestSeries(testSeriesResponse.data);
      }

      if (sectionsResponse?.success) {
        setNestedFromSections(Boolean(sectionsResponse.nested_sections));
        // The list carries each section's question count; no request per section
        setSections(
          (sectionsResponse.data || []).map((section) => ({
            ...section,
            questions_added: section.questions_count ?? (section.questions || []).length,
          }))
        );
      } else if (sectionsResponse) {
        setError(sectionsResponse.message || "Failed to load sections.");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, fetchData]);

  // Subject-wise test: subjects (Physics…) hold sections (Section A…), and only sections hold questions
  const nested =
    Boolean(testSeries?.nested_sections) || nestedFromSections || sections.some((s) => s.parent_id != null);
  const subjects = nested ? sections.filter((s) => s.parent_id == null) : [];
  const childrenOf = (subjectId) => sections.filter((s) => s.parent_id === subjectId);
  // The sections that hold questions (all of them in a plain test)
  const questionSections = nested ? sections.filter((s) => s.parent_id != null) : sections;

  const editingSubject = editing?.kind === "subject";
  const editingSection = editing?.section || null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const openModal = (next, values = EMPTY_FORM) => {
    setEditing(next);
    setFormData(values);
    setFormErrors({});
    setIsModalOpen(true);
  };

  // A section of a plain test, or (with parent) a section inside a subject
  const openCreateSection = (parent = null) => openModal({ kind: "section", parent });
  const openCreateSubject = () => openModal({ kind: "subject" });

  const openEditModal = (section) =>
    openModal(
      {
        kind: nested && section.parent_id == null ? "subject" : "section",
        section,
        parent: sections.find((s) => s.id === section.parent_id) || null,
      },
      {
        name: section.name || "",
        description: section.description || "",
        duration_minutes: section.duration_minutes ?? section.duration ?? "",
        total_questions: section.total_questions || section.questions || "",
      }
    );

  const closeModal = useCallback(() => {
    if (saving) return;
    setIsModalOpen(false);
    setEditing(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  }, [saving]);

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = `${editingSubject ? "Subject" : "Section"} name is required.`;
    if (!editingSubject) {
      // With one timer for the whole test, a section duration is optional but must be valid if typed
      const raw = String(formData.duration_minutes ?? "").trim();
      const duration = parseInt(raw, 10);
      if (sectionalTiming ? !duration || duration < 1 : raw !== "" && (!duration || duration < 1)) {
        next.duration_minutes = "Enter a duration of at least 1 minute.";
      }
      const total = parseInt(formData.total_questions, 10);
      if (!total || total < 1) next.total_questions = "Enter at least 1 question.";
    }
    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !validate()) return;

    const rawDuration = String(formData.duration_minutes ?? "").trim();
    const description = isBlankHtml(formData.description) ? "" : formData.description;
    // A subject is a name and instructions; its sections carry the timer and question target
    const payload = editingSubject
      ? { name: formData.name.trim(), description }
      : {
          name: formData.name.trim(),
          description,
          duration_minutes: rawDuration === "" ? null : parseInt(rawDuration, 10),
          total_questions: parseInt(formData.total_questions, 10),
        };
    const noun = editingSubject ? "subject" : "section";

    setSaving(true);
    try {
      const response = editingSection
        ? await updateSectionAPI(editingSection.id, payload)
        : await createSectionAPI({
            ...payload,
            test_series_id: parseInt(id, 10),
            ...(editing?.parent ? { parent_id: editing.parent.id } : {}),
          });

      if (response?.success) {
        if (editingSection) {
          // Keep the locally computed question count when merging the updated section
          setSections((prev) =>
            prev.map((s) =>
              s.id === editingSection.id
                ? { ...s, ...payload, ...(response.data || {}), questions_added: s.questions_added }
                : s
            )
          );
        } else {
          const created = { ...payload, ...(response.data || {}), questions_added: 0 };
          setSections((prev) => {
            if (!editing?.parent) return [...prev, created];
            // A new section goes after the last section of its subject
            const parentId = editing.parent.id;
            const lastIndex = prev.reduce(
              (last, s, i) => (s.id === parentId || s.parent_id === parentId ? i : last),
              prev.length - 1
            );
            return [...prev.slice(0, lastIndex + 1), created, ...prev.slice(lastIndex + 1)];
          });
        }
        showSuccess(
          `"${payload.name}" was ${editingSection ? "updated" : "created"}.`,
          `${noun.charAt(0).toUpperCase()}${noun.slice(1)} ${editingSection ? "updated" : "created"}`
        );
        setIsModalOpen(false);
        setEditing(null);
        setFormData(EMPTY_FORM);
      } else if (response) {
        const fieldErrors = Object.fromEntries(
          ["name", "duration_minutes", "total_questions", "parent_id"]
            .filter((field) => response.errors?.[field]?.length)
            .map((field) => [field, [].concat(response.errors[field])[0]])
        );
        if (fieldErrors.parent_id) showError(fieldErrors.parent_id);
        else if (Object.keys(fieldErrors).length) setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
        else showError(response.message || `Failed to ${editingSection ? "update" : "create"} the ${noun}.`);
      }
    } catch (err) {
      console.error("Error saving section:", err);
      showError(`Failed to ${editingSection ? "update" : "create"} the ${noun}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (section) => {
    const isSubject = nested && section.parent_id == null;
    const inner = isSubject ? childrenOf(section.id) : [];
    const result = await showDeleteConfirmation(
      isSubject ? `subject${inner.length ? ` and its ${inner.length} section(s)` : ""}` : "section",
      section.name
    );
    if (!result.isConfirmed) return;

    try {
      const response = await deleteSectionAPI(section.id);
      if (response?.success) {
        setSections((prev) => prev.filter((s) => s.id !== section.id && s.parent_id !== section.id));
        showSuccess(`"${section.name}" was deleted.`, isSubject ? "Subject deleted" : "Section deleted");
      } else if (response) {
        showError(response.message || "Failed to delete it.");
      }
    } catch (err) {
      console.error("Error deleting section:", err);
      showError("Failed to delete it. Please try again.");
    }
  };

  // Move a section up (-1) or down (+1) within its group (the test's sections, its subjects, or one
  // subject's sections); saved straight away, put back if the save fails
  const [reordering, setReordering] = useState(false);
  const moveWithin = async (group, index, delta, rebuild) => {
    const target = index + delta;
    if (reordering || target < 0 || target >= group.length) return;

    const previous = sections;
    const nextGroup = swapped(group, index, target);
    setSections(rebuild(nextGroup));
    setReordering(true);

    try {
      const response = await reorderSectionsAPI(parseInt(id, 10), nextGroup.map((s) => s.id));
      if (response && !response.success) {
        setSections(previous);
        showError(response.message || "Couldn't save the new order.");
      }
    } catch {
      setSections(previous);
      showError("Couldn't save the new order. Please try again.");
    } finally {
      setReordering(false);
    }
  };

  const moveSection = (index, delta) => moveWithin(sections, index, delta, (next) => next);
  const moveSubject = (index, delta) =>
    moveWithin(subjects, index, delta, (next) => next.flatMap((s) => [s, ...childrenOf(s.id)]));
  const moveChild = (subjectId, index, delta) =>
    moveWithin(childrenOf(subjectId), index, delta, (next) =>
      subjects.flatMap((s) => [s, ...(s.id === subjectId ? next : childrenOf(s.id))])
    );

  const handleExport = async () => {
    try {
      setExporting(true);
      // With section settings, so the file can be edited and re-imported with "Update section settings"
      await exportQuestionsCsv({ testSeriesId: id, withSections: true });
    } catch (err) {
      showError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const totalsOf = (list) =>
    list.reduce(
      (acc, s) => ({
        duration: acc.duration + durationOf(s),
        target: acc.target + targetOf(s),
        added: acc.added + (Number(s.questions_added) || 0),
      }),
      { duration: 0, target: 0, added: 0 }
    );
  const totals = totalsOf(questionSections);

  const seriesTitle = testSeries?.title || testSeries?.name;
  // Older data has no flag and is section-wise
  const sectionalTiming = testSeries?.sectional_timing !== false;
  // What students get (total_duration_minutes). Section-wise, that is the sum of the sections; the live sum
  // stays correct after sections are added or edited here, while the loaded value would be stale.
  const totalMinutes = sectionalTiming
    ? totals.duration
    : Number(testSeries?.total_duration_minutes ?? testSeries?.duration_minutes) || 0;

  const toggleDescription = (sectionId) =>
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));

  const renderDescription = (section) =>
    !isBlankHtml(section.description) && (
      <div className="section-description">
        <div
          className={`section-description-content ${expandedSections[section.id] ? "expanded" : ""}`}
          dangerouslySetInnerHTML={{ __html: section.description }}
        />
        <button type="button" className="section-description-toggle" onClick={() => toggleDescription(section.id)}>
          {expandedSections[section.id] ? "Show less" : "Show description"}
          <i className={`bi bi-chevron-${expandedSections[section.id] ? "up" : "down"}`}></i>
        </button>
      </div>
    );

  const renderOrder = (name, index, count, onMove, label) => (
    <div className="section-order">
      <button
        type="button"
        className="section-order-btn"
        onClick={() => onMove(index, -1)}
        disabled={index === 0 || reordering}
        title="Move up"
        aria-label={`Move ${name} up`}
      >
        <i className="bi bi-chevron-up"></i>
      </button>
      <span className="section-index">{label ?? index + 1}</span>
      <button
        type="button"
        className="section-order-btn"
        onClick={() => onMove(index, 1)}
        disabled={index === count - 1 || reordering}
        title="Move down"
        aria-label={`Move ${name} down`}
      >
        <i className="bi bi-chevron-down"></i>
      </button>
    </div>
  );

  // One section that holds questions: a card in a plain test, a row inside its subject otherwise
  const renderSectionCard = (section, index, count, onMove, label) => {
    const target = targetOf(section);
    const added = Number(section.questions_added) || 0;
    const percent = target > 0 ? Math.min(100, Math.round((added / target) * 100)) : 0;
    const complete = target > 0 && added >= target;

    return (
      <article key={section.id} className={`section-card card ${nested ? "section-card--nested" : ""}`}>
        <div className="section-card-main">
          {renderOrder(section.name, index, count, onMove, label)}

          <div className="section-card-body">
            <div className="section-card-heading">
              <h3>{section.name}</h3>
              {complete && (
                <span className="badge badge-success">
                  <i className="bi bi-check2"></i> Complete
                </span>
              )}
            </div>
            <div className="section-meta">
              <span>
                <i className="bi bi-stopwatch"></i> {durationOf(section) ? `${durationOf(section)} min` : "—"}
              </span>
              <span>
                <i className="bi bi-list-ol"></i> {target} questions
              </span>
            </div>

            <div className="section-progress">
              <div className="progress">
                <div className={`progress-fill ${complete ? "complete" : ""}`} style={{ width: `${percent}%` }} />
              </div>
              <span>
                <strong>{added}</strong> / {target} added
              </span>
            </div>

            {renderDescription(section)}
          </div>
        </div>

        <div className="section-actions">
          <Link to={`/admin/test-series/${id}/section/${section.id}/add-question`} className="btn-primary btn-sm">
            <i className="bi bi-plus-lg"></i> Add question
          </Link>
          <Link to={`/admin/test-series/${id}/section/${section.id}/questions`} className="btn-secondary btn-sm">
            <i className="bi bi-eye"></i> View questions
          </Link>
          <button
            type="button"
            onClick={() => openEditModal(section)}
            className="btn-secondary btn-icon btn-sm"
            title="Edit section"
            aria-label="Edit section"
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            type="button"
            onClick={() => handleDelete(section)}
            className="btn-danger btn-icon btn-sm"
            title="Delete section"
            aria-label="Delete section"
          >
            <i className="bi bi-trash3"></i>
          </button>
        </div>
      </article>
    );
  };

  const renderSubject = (subject, index) => {
    const inner = childrenOf(subject.id);
    const t = totalsOf(inner);

    return (
      <section key={subject.id} className="subject-block card">
        <header className="subject-header">
          <div className="section-card-main">
            {renderOrder(subject.name, index, subjects.length, moveSubject)}
            <div className="section-card-body">
              <div className="section-card-heading">
                <span className="badge badge-neutral">
                  <i className="bi bi-collection"></i> Subject
                </span>
                <h3>{subject.name}</h3>
              </div>
              <div className="section-meta">
                <span>
                  <i className="bi bi-layers"></i> {inner.length} {inner.length === 1 ? "section" : "sections"}
                </span>
                {sectionalTiming && (
                  <span>
                    <i className="bi bi-stopwatch"></i> {t.duration} min
                  </span>
                )}
                <span>
                  <i className="bi bi-check2-circle"></i> {t.added} / {t.target} questions added
                </span>
              </div>
              {renderDescription(subject)}
            </div>
          </div>
          <div className="section-actions">
            <button type="button" className="btn-primary btn-sm" onClick={() => openCreateSection(subject)}>
              <i className="bi bi-plus-lg"></i> Add section
            </button>
            <button
              type="button"
              onClick={() => openEditModal(subject)}
              className="btn-secondary btn-icon btn-sm"
              title="Edit subject"
              aria-label={`Edit ${subject.name}`}
            >
              <i className="bi bi-pencil"></i>
            </button>
            <button
              type="button"
              onClick={() => handleDelete(subject)}
              className="btn-danger btn-icon btn-sm"
              title="Delete subject"
              aria-label={`Delete ${subject.name}`}
            >
              <i className="bi bi-trash3"></i>
            </button>
          </div>
        </header>

        {inner.length === 0 ? (
          <div className="subject-empty">
            <i className="bi bi-info-circle"></i> No sections yet. Questions go into sections, so add one (e.g.
            Section A) to {subject.name}.
          </div>
        ) : (
          <div className="subject-sections">
            {inner.map((section, i) =>
              renderSectionCard(section, i, inner.length, (at, delta) => moveChild(subject.id, at, delta), `${index + 1}.${i + 1}`)
            )}
          </div>
        )}
      </section>
    );
  };

  const modalNoun = editingSubject ? "subject" : "section";

  return (
    <div className="sections-manager page">
      <div className="page-header">
        <div>
          <button type="button" className="back-link" onClick={() => navigate("/admin/test-series")}>
            <i className="bi bi-arrow-left"></i> Test series
          </button>
          <h2>{seriesTitle || "Sections"}</h2>
          <p>
            {nested
              ? `Organise this test into subjects, split each subject into ${sectionalTiming ? "timed " : ""}sections, then add questions to each section.`
              : `Organise this test series into ${sectionalTiming ? "timed " : ""}sections, then add questions to each.`}
          </p>
        </div>
        <div className="header-actions">
          <Link to={`/admin/test-series/${id}/edit`} className="btn-secondary">
            <i className="bi bi-pencil"></i> Edit details
          </Link>
          <SampleCsvMenu nested={nested} sectionalTiming={sectionalTiming} />
          <button type="button" className="btn-ghost" onClick={() => setHistoryOpen(true)}>
            <i className="bi bi-clock-history"></i> Import history
          </button>
          <button type="button" className="btn-secondary" onClick={() => setImportOpen(true)}>
            <i className="bi bi-upload"></i> Import CSV
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleExport}
            disabled={sections.length === 0 || exporting}
          >
            {exporting ? <span className="btn-spinner"></span> : <i className="bi bi-download"></i>} Export CSV
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => (nested ? openCreateSubject() : openCreateSection())}
          >
            <i className="bi bi-plus-lg"></i> {nested ? "Add subject" : "Add section"}
          </button>
        </div>
      </div>

      {importOpen && (
        <ImportQuestionsModal
          testSeriesId={id}
          scopeLabel={seriesTitle}
          sectionNames={questionSections.map((s) => s.name)}
          sectionalTiming={sectionalTiming}
          nestedSections={nested}
          defaultCreateSections
          onClose={() => setImportOpen(false)}
          onImported={fetchData}
        />
      )}

      {historyOpen && (
        <ImportHistoryModal
          testSeriesId={id}
          scopeLabel={seriesTitle}
          onClose={() => setHistoryOpen(false)}
          onChanged={fetchData}
        />
      )}

      <ErrorAlert message={error} onRetry={fetchData} />

      {testSeries && nested && (
        <div className="alert alert-info">
          <i className="bi bi-diagram-3"></i>
          <div className="alert-body">
            This test is split into <strong>subjects</strong>, and each subject into <strong>sections</strong>.
            Questions go into sections. For CSV import download <strong>Sample CSV → Subjects → sections sample</strong>{" "}
            (<code>subjects_sections_questions_sample.csv</code>): every row names its <code>subject</code> and its{" "}
            <code>section</code>.
          </div>
        </div>
      )}

      {testSeries && !sectionalTiming && (
        <div className="alert alert-info">
          <i className="bi bi-stopwatch"></i>
          <div className="alert-body">
            This test has one timer of <strong>{totalMinutes} min</strong> for the
            whole test. Section durations are optional and are not used by the timer.
          </div>
          <div className="alert-actions">
            <Link to={`/admin/test-series/${id}/edit`} className="btn-secondary btn-sm">
              <i className="bi bi-pencil"></i> Change timing
            </Link>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingState message="Loading sections…" />
      ) : (
        <>
          {sections.length > 0 && (
            <div className="stats-grid">
              {nested ? (
                <StatCard icon="bi bi-collection" tone="brand" value={subjects.length} label="Subjects" />
              ) : (
                <StatCard icon="bi bi-layers" tone="brand" value={sections.length} label="Sections" />
              )}
              <StatCard icon="bi bi-stopwatch" tone="info" value={`${totalMinutes} min`} label="Total duration" />
              <StatCard
                icon="bi bi-list-ol"
                tone="warning"
                value={totals.target}
                label="Planned questions"
                hint={nested ? `${questionSections.length} sections` : undefined}
              />
              <StatCard
                icon="bi bi-check2-circle"
                tone="success"
                value={totals.added}
                label="Questions added"
                hint={totals.target ? `${Math.round((totals.added / totals.target) * 100)}% complete` : undefined}
              />
            </div>
          )}

          {sections.length === 0 ? (
            !error && (
              <EmptyState
                icon={nested ? "bi bi-diagram-3" : "bi bi-layers"}
                title={nested ? "No subjects yet" : "No sections yet"}
                message={
                  nested
                    ? `Add subjects such as Physics, Chemistry and Maths, then split each into sections such as Section A and Section B${
                        sectionalTiming ? ", each with its own timer" : ""
                      }. Or set up the whole test — subjects, sections and questions — from one CSV file.`
                    : `Sections split a test into parts, such as Physics, Chemistry and Maths${
                        sectionalTiming ? ", each with its own timer" : ""
                      }. Add them one by one, or set up the whole test — sections and questions — from one CSV file.`
                }
                action={
                  <div className="sm-empty-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => (nested ? openCreateSubject() : openCreateSection())}
                    >
                      <i className="bi bi-plus-lg"></i> {nested ? "Add subject" : "Add section"}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setImportOpen(true)}>
                      <i className="bi bi-upload"></i> {nested ? "Import from CSV" : "Import sections from CSV"}
                    </button>
                    <SampleCsvMenu
                      nested={nested}
                      sectionalTiming={sectionalTiming}
                      label={nested ? "Download subjects → sections sample" : "Download sample CSV"}
                    />
                  </div>
                }
              />
            )
          ) : nested ? (
            <div className="section-list">{subjects.map(renderSubject)}</div>
          ) : (
            <div className="section-list">
              {sections.map((section, index) => renderSectionCard(section, index, sections.length, moveSection))}
            </div>
          )}
        </>
      )}

      <Modal
        open={isModalOpen}
        onClose={closeModal}
        closeOnOverlay={false}
        size="lg"
        icon={editingSection ? "bi bi-pencil-square" : editingSubject ? "bi bi-collection" : "bi bi-layers"}
        title={editingSection ? `Edit ${modalNoun}` : `Add a ${modalNoun}`}
        subtitle={
          editing?.parent ? `In ${editing.parent.name}` : seriesTitle ? `In ${seriesTitle}` : undefined
        }
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </button>
            <button type="submit" form="sectionForm" className="btn-primary" disabled={saving}>
              {saving ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
              {editingSection ? "Save changes" : `Create ${modalNoun}`}
            </button>
          </>
        }
      >
        <form id="sectionForm" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="sectionNameInput">
              {editingSubject ? "Subject name" : "Section name"} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="sectionNameInput"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={
                editingSubject
                  ? "e.g. Physics, Chemistry, Mathematics"
                  : editing?.parent
                  ? "e.g. Section A, Section B"
                  : "e.g. Physics, Chemistry, Mathematics"
              }
              className={formErrors.name ? "error" : ""}
              autoFocus
            />
            {formErrors.name && <span className="field-error">{formErrors.name}</span>}
          </div>

          {!editingSubject && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sectionDurationInput">
                  {sectionalTiming ? (
                    <>
                      Duration (minutes) <span className="required">*</span>
                    </>
                  ) : (
                    "Duration (minutes, optional)"
                  )}
                </label>
                <input
                  type="number"
                  id="sectionDurationInput"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleInputChange}
                  placeholder={sectionalTiming ? "60" : "Not used by the timer"}
                  min="1"
                  className={formErrors.duration_minutes ? "error" : ""}
                />
                {formErrors.duration_minutes && <span className="field-error">{formErrors.duration_minutes}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="sectionQuestionsInput">
                  Total questions <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="sectionQuestionsInput"
                  name="total_questions"
                  value={formData.total_questions}
                  onChange={handleInputChange}
                  placeholder="30"
                  min="1"
                  className={formErrors.total_questions ? "error" : ""}
                />
                {formErrors.total_questions && <span className="field-error">{formErrors.total_questions}</span>}
              </div>
            </div>
          )}

          {editingSubject && (
            <p className="help-text">
              A subject groups its sections. Its sections carry the {sectionalTiming ? "timer and the " : ""}question
              target; add them after creating the subject.
            </p>
          )}

          <div className="form-group" style={{ marginTop: 20 }}>
            <label>
              {editingSubject ? "Subject description" : "Instructions / description"}{" "}
              <span className="optional">Optional</span>
            </label>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
              placeholder={
                editingSubject
                  ? "What students should know about this subject, e.g. how its sections are marked…"
                  : "Instructions students see before starting this section…"
              }
              className="rich-editor"
              modules={QUILL_MODULES}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default SectionsManager;
